// <div data-controller="components--subscribe-modal">
//
// </div>
import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";
import helpers from "@/utils/helpers";
import { t } from "@/utils/i18n";

export default class extends Controller {
  static targets = [
    "form", "modal", "name", "email", "pin",
    "confirmButtons", "authenticationField",
    "hiddenSubmit", "pinNotification",
    "confirmFreePlan", "confirmPaidPlan",
    "privacyAgreement", "adultAgreement", "autoPayAgreement",
    "paymentMethodArea"
  ];

  initialize() {
    this.pinSent = false;
  }

  showModal(initialPlan = undefined) {
    // 새로고침/공유 시에도 모달이 열린 채 유지되도록 pop 파라미터 기록
    // 유료 플랜 오픈이면 pop=membership 으로 유지해 새로고침 시에도 유료 옵션이 선택되게 한다
    const url = new URL(window.location.href);
    url.searchParams.set('pop', initialPlan === "paid" ? 'membership' : 'up');
    // Turbo가 저장한 history state(restorationIdentifier 등)를 보존해야 한다.
    // {} 로 덮으면 해당 항목의 turbo 키가 사라져 뒤로/앞으로 시 Turbo가 popstate를 무시 → 화면 고정 버그.
    window.history.replaceState(window.history.state, '', url);

    if (initialPlan === "paid") {
      // 작가가 월간 플랜을 비활성화한 경우 등 radio가 없을 수 있으므로 방어
      const monthlyRadio = this.formTarget.querySelector('input[name="subscription[plan]"][value="monthly"]');
      if (monthlyRadio) {
        monthlyRadio.checked = true;
        this.forPaidPlan();
      }
    }
    this.modalTarget.style.removeProperty("display");
    // document.body.classList.add("overflow-hidden");

    // AI 채팅 패널을 닫기 위한 커스텀 이벤트 발생
    window.dispatchEvent(new CustomEvent('subscribe-modal:opened'));
  }

  hideModal() {
    const url = new URL(window.location.href);
    url.searchParams.delete('pop');
    // Turbo history state 보존 (showModal 주석 참고)
    window.history.replaceState(window.history.state, '', url);

    this.modalTarget.style.display = "none";
    // document.body.classList.remove("overflow-hidden");

    // AI 채팅 FAB 버튼을 다시 표시하기 위한 커스텀 이벤트 발생
    window.dispatchEvent(new CustomEvent('subscribe-modal:closed'));
  }

  get autoPayAgreementContainer() {
    return document.getElementById("autoPayAgreement");
  }

  forFreePlan() {
    if (this.hasConfirmPaidPlanTarget) {
      this.confirmPaidPlanTarget.classList.add("hidden");
    }
    if (this.hasConfirmFreePlanTarget) {
      this.confirmFreePlanTarget.classList.remove("hidden");
    }
    if (this.hasAutoPayAgreementTarget) {
      this.autoPayAgreementTarget.required = false;
      this.autoPayAgreementContainer.classList.add("hidden");
    }

    if (this.hasPaymentMethodAreaTarget) {
      this.paymentMethodAreaTarget.classList.add("hidden");
    }
  }

  forPaidPlan() {
    if (this.hasConfirmFreePlanTarget) {
      this.confirmFreePlanTarget.classList.add("hidden");
    }
    if (this.hasConfirmPaidPlanTarget) {
      this.confirmPaidPlanTarget.classList.remove("hidden");
    }
    if (this.hasAutoPayAgreementTarget) {
      this.autoPayAgreementTarget.required = true;
      this.autoPayAgreementContainer.classList.remove("hidden");
    }

    if (this.hasPaymentMethodAreaTarget) {
      this.paymentMethodAreaTarget.classList.remove("hidden");
    }
  }

  submitForm(e) {
    // 폼 유효성 검사 먼저 실행 (필수 체크박스 등)
    if (!this.formTarget.checkValidity()) {
      this.formTarget.reportValidity();
      return;
    }

    const button = e?.currentTarget;
    if (button) {
      button.disabled = true;
      button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="animate-spin h-5 w-5 mx-auto" viewBox="0 0 24 24" fill="currentColor"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><line x1="12" y1="6" x2="12" y2="3" stroke="currentColor" stroke-width="2"/><line x1="6" y1="12" x2="3" y2="12" stroke="currentColor" stroke-width="2"/><line x1="7.75" y1="7.75" x2="5.6" y2="5.6" stroke="currentColor" stroke-width="2"/></svg>';
    }
    this.hiddenSubmitTarget.click();
  }

  startAuthentication(e) {
    if (!this.emailTarget.checkValidity()) {
      this.emailTarget.focus();
      return false;
    }

    if (this.hasPrivacyAgreementTarget) {
      if (!this.privacyAgreementTarget.checkValidity()) {
        this.privacyAgreementTarget.focus();
        return false;
      }
    }

    if (this.hasAdultAgreementTarget) {
      if (!this.adultAgreementTarget.checkValidity()) {
        this.adultAgreementTarget.focus();
        return false;
      }
    }

    if (this.hasAutoPayAgreementTarget) {
      if (!this.autoPayAgreementTarget.checkValidity()) {
        this.autoPayAgreementTarget.focus();
        return false;
      }
    }

    const button = e?.currentTarget;
    const originalButtonHtml = button?.innerHTML;
    if (button) {
      button.disabled = true;
      button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="animate-spin h-5 w-5 mx-auto" viewBox="0 0 24 24" fill="currentColor"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><line x1="12" y1="6" x2="12" y2="3" stroke="currentColor" stroke-width="2"/><line x1="6" y1="12" x2="3" y2="12" stroke="currentColor" stroke-width="2"/><line x1="7.75" y1="7.75" x2="5.6" y2="5.6" stroke="currentColor" stroke-width="2"/></svg>';
    }

    this.emailTarget.readOnly = true;
    // 발행인이 구독자 닉네임 입력란을 숨긴 경우 name target 이 없다
    if (this.hasNameTarget) this.nameTarget.readOnly = true;
    api.post("/api/users/send_pin", {email: this.emailTarget.value, name: this.currentName()}).then((res) => {
      if (res.status === 200) {
        this.pinSent = true;
        if (button) {
          button.disabled = false;
          button.innerHTML = originalButtonHtml;
        }
        this.confirmButtonsTarget.style.display = "none";
        this.authenticationFieldTarget.classList.remove("hidden");
        this.pinTarget.focus();
      } else if (res.status === 201) { // 대기 필요
        if (button) {
          button.disabled = false;
          button.innerHTML = originalButtonHtml;
        }
        alert(res.data.msg);
      } else {
        this.emailTarget.readOnly = false;
        if (this.hasNameTarget) this.nameTarget.readOnly = false;
        if (button) {
          button.disabled = false;
          button.innerHTML = originalButtonHtml;
        }
        alert(res.data.msg);
      }
    }).catch((e) => {
      const { msg } = e.response.data;
      this.emailTarget.readOnly = false;
      if (this.hasNameTarget) this.nameTarget.readOnly = false;
      if (button) {
        button.disabled = false;
        button.innerHTML = originalButtonHtml;
      }
      alert(msg);
      this.emailTarget.focus();
    })
  }

  currentName() {
    return this.hasNameTarget ? this.nameTarget.value : undefined;
  }

  resendPin(e) {
    e.target.classList.remove("text-green-700");
    e.target.classList.add("text-yellow-500");
    e.target.innerText = t("pin.sending", "발송중..");
    api.post("/api/users/send_pin", {email: this.emailTarget.value, name: this.currentName()}).then((res) => {
      if (res.status === 200) {
        this.pinSent = true;
        e.target.classList.remove("text-yellow-500", "cursor-pointer");
        e.target.classList.add("text-green-700");
        e.target.innerText = t("pin.resent", "재발송완료 (스팸함도 확인해주세요)");
        e.target.dataset.action = null;
        this.pinTarget.focus();
        this.authenticationFieldTarget.classList.remove("hidden");
      } else {
        e.target.classList.remove("text-yellow-700");
        e.target.classList.add("text-red-700");
        e.target.innerText = res.data.msg || t("pin.failed", "발송실패 (재시도 하기)");
        this.confirmButtonsTarget.style.removeProperty("display");
      }
    })
  }



  connect() {
    const initialPlan = this.formTarget.querySelector('input[name="subscription[plan]"]:checked');
    if (initialPlan) {
      if (initialPlan.value === "free" || initialPlan.value === "gifted") {
        if (this.hasConfirmFreePlanTarget) {
          this.forFreePlan();
        }
      } else {
        this.forPaidPlan();
      }
    }

    if (this.data.get("initial-status") === "true") {
      // pop=membership 랜딩: 유료 플랜이 선택된 상태로 모달 오픈
      this.showModal(this.data.get("initial-plan") === "paid" ? "paid" : undefined);
    }

    this.formTarget.addEventListener("keydown", (e) => {
      const keyCode = e.keyCode || e.which;
      const selectedPlan = this.formTarget.querySelector('input[name="subscription[plan]"]:checked').value;

      if (keyCode === 13 && !this.pinSent) {
        if (selectedPlan === "monthly" || selectedPlan === "yearly") {
          e.preventDefault();
          this.startAuthentication();
          return false;
        }
      }
    })
  }
}